import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import { Plus, RefreshCw, FileSignature, Search, X, ChevronRight, Trash2, Printer } from 'lucide-react';
import api from '../../utils/api';
import usePermission from '../../hooks/usePermission';
import { useSettings } from '../../context/SettingsContext';
import { buildQuotationHtml } from './quotationPrint';

export const QUOTE_STATUS = {
  draft: { label: 'Draft', cls: 'bg-amber-100 text-amber-700 border-amber-300' },
  sent: { label: 'Sent', cls: 'bg-blue-100 text-blue-700 border-blue-300' },
  accepted: { label: 'Accepted', cls: 'bg-green-100 text-green-700 border-green-300' },
  rejected: { label: 'Rejected', cls: 'bg-red-100 text-red-700 border-red-300' },
  revised: { label: 'Revised', cls: 'bg-gray-100 text-gray-600 border-gray-300' },
};
const money = n => `₹ ${Number(n || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fmtDate = d => (d ? new Date(d).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : '—');

export default function QuotationListPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const [params] = useSearchParams();
  const base = location.pathname.startsWith('/worker') ? '/worker/quotations' : '/admin/quotations';
  const { canWrite, canDelete } = usePermission('quotations');
  const moFilter = params.get('mo') || '';
  const [rows, setRows] = useState([]);
  const [counts, setCounts] = useState({});
  const [status, setStatus] = useState('all');
  const [q, setQ] = useState('');
  const [loading, setLoading] = useState(true);
  const [picking, setPicking] = useState(false);
  const { settings } = useSettings() || {};

  // Print straight from the list: open the window on the click (so pop-up blockers allow it), then fill it
  const print = async r => {
    const w = window.open('', '_blank');
    if (!w) return toast.error('Allow pop-ups to print');
    w.document.write('<p style="font-family:Arial;padding:24px;color:#666">Preparing quotation…</p>');
    try {
      const res = await api.get(`/quotations/${r.id}`);
      w.document.open();
      w.document.write(buildQuotationHtml(res.data, settings || {}, window.location.origin));
      w.document.close(); w.focus();
    } catch (err) {
      w.close();
      toast.error(err.response?.data?.message || 'Could not load the quotation');
    }
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get('/quotations', { params: { status: status === 'all' ? undefined : status, main_po_id: moFilter || undefined } });
      setRows(r.data.rows || []);
      const m = {}; (r.data.counts || []).forEach(c => { m[c.status] = c.c; });
      m.all = Object.values(m).reduce((a, b) => a + b, 0);
      setCounts(m);
    } catch (err) { toast.error(err.response?.data?.message || 'Failed to load'); }
    finally { setLoading(false); }
  }, [status, moFilter]);
  useEffect(() => { load(); }, [load]);
  // "New Quotation" from a Manufacturing Order page
  useEffect(() => { if (params.get('new') === '1' && moFilter) navigate(`${base}/new?mo=${moFilter}`, { replace: true }); }, [params, moFilter, base, navigate]);

  const shown = useMemo(() => {
    const w = q.trim().toLowerCase().split(/\s+/).filter(Boolean);
    if (!w.length) return rows;
    return rows.filter(r => w.every(x => [r.quote_no, r.client_name, r.po_no, r.project_name, r.project_code, r.subject].join(' ').toLowerCase().includes(x)));
  }, [rows, q]);

  const del = async r => {
    if (!window.confirm(`Delete draft ${r.quote_no}${r.revision ? ` Rev ${r.revision}` : ''}?`)) return;
    try { await api.delete(`/quotations/${r.id}`); toast.success('Deleted'); load(); }
    catch (err) { toast.error(err.response?.data?.message || 'Delete failed'); }
  };

  return (
    <div className="p-4 md:p-6 space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-xl md:text-2xl font-bold text-gray-800 flex items-center gap-2"><FileSignature className="w-6 h-6 text-blue-600" /> Quotations</h1>
          <p className="text-sm text-gray-500">Price quotations against Manufacturing Orders — rate × qty + GST, with revisions</p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={load} className="p-2 rounded-lg border border-gray-200 hover:bg-gray-50"><RefreshCw className={`w-4 h-4 text-gray-500 ${loading ? 'animate-spin' : ''}`} /></button>
          {canWrite && <button onClick={() => setPicking(true)} className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-semibold"><Plus className="w-4 h-4" /> New Quotation</button>}
        </div>
      </div>

      {moFilter && (
        <div className="flex items-center gap-2 text-sm bg-blue-50 border border-blue-200 rounded-lg px-3 py-2 text-blue-800">
          Showing quotations of one Manufacturing Order
          <button onClick={() => navigate(base)} className="ml-auto text-xs font-semibold underline">Show all</button>
        </div>
      )}

      <div className="flex flex-wrap items-center gap-2">
        {['all', 'draft', 'sent', 'accepted', 'rejected'].map(t => (
          <button key={t} onClick={() => setStatus(t)}
            className={`px-3 py-1.5 rounded-full text-xs font-semibold border ${status === t ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50'}`}>
            {t === 'all' ? 'All' : QUOTE_STATUS[t].label} {counts[t] !== undefined ? `(${counts[t]})` : ''}
          </button>
        ))}
        <span className="ml-auto flex items-center gap-1.5 border border-gray-200 bg-white rounded-lg px-2.5 py-1.5 w-full sm:w-64">
          <Search className="w-4 h-4 text-gray-400" />
          <input value={q} onChange={e => setQ(e.target.value)} placeholder="Number, client, order…" className="flex-1 min-w-0 text-sm outline-none" />
        </span>
      </div>

      <div className="bg-white rounded-xl border border-gray-200 overflow-x-auto">
        {loading ? <div className="p-10 text-center text-gray-400 text-sm">Loading…</div>
          : !shown.length ? <div className="p-10 text-center text-gray-400 text-sm">No quotations{canWrite ? ' — use New Quotation to start one' : ''}</div>
          : (
            <table className="w-full text-sm min-w-[860px]">
              <thead><tr className="bg-gray-50 text-left text-xs uppercase text-gray-500">
                <th className="px-4 py-3">Quotation</th><th className="px-4 py-3">Client</th><th className="px-4 py-3">Manufacturing Order / Project</th>
                <th className="px-4 py-3">Date</th><th className="px-4 py-3 text-right">Total</th><th className="px-4 py-3">Status</th><th className="px-4 py-3"></th>
              </tr></thead>
              <tbody className="divide-y divide-gray-100">
                {shown.map(r => (
                  <tr key={r.id} className="hover:bg-gray-50 cursor-pointer" onClick={() => navigate(`${base}/${r.id}`)}>
                    <td className="px-4 py-3"><div className="font-semibold text-blue-700 whitespace-nowrap">{r.quote_no}{r.revision ? <span className="ml-1 text-xs text-gray-500">Rev {r.revision}</span> : null}</div>
                      <div className="text-xs text-gray-400">{r.item_count} item{r.item_count === 1 ? '' : 's'}</div></td>
                    <td className="px-4 py-3 text-gray-700">{r.client_name || '—'}</td>
                    <td className="px-4 py-3"><div className="text-gray-800">{r.po_no || '—'}</div><div className="text-xs text-gray-400">{[r.project_name, r.project_code].filter(Boolean).join(' · ')}</div></td>
                    <td className="px-4 py-3 text-gray-600 whitespace-nowrap">{fmtDate(r.quote_date)}</td>
                    <td className="px-4 py-3 text-right font-bold text-gray-800 whitespace-nowrap">{money(r.total_amount)}</td>
                    <td className="px-4 py-3"><span className={`px-2 py-0.5 rounded-full border text-xs font-semibold ${QUOTE_STATUS[r.status]?.cls || ''}`}>{QUOTE_STATUS[r.status]?.label || r.status}</span></td>
                    <td className="px-4 py-3" onClick={e => e.stopPropagation()}>
                      <div className="flex justify-end items-center gap-1">
                        <button onClick={() => print(r)} title="Print" className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-600"><Printer className="w-4 h-4" /></button>
                        {canDelete && r.status === 'draft' && <button onClick={() => del(r)} title="Delete draft" className="p-1.5 rounded-lg hover:bg-red-50 text-red-600"><Trash2 className="w-4 h-4" /></button>}
                        <ChevronRight className="w-4 h-4 text-gray-300" />
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
      </div>

      {picking && <MoPicker onClose={() => setPicking(false)} onPick={id => navigate(id ? `${base}/new?mo=${id}` : `${base}/new`)} />}
    </div>
  );
}

// Pick the Manufacturing Order a new quotation is for
function MoPicker({ onClose, onPick }) {
  const [list, setList] = useState([]);
  const [q, setQ] = useState('');
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    api.get('/quotations/mo-options').then(r => setList(r.data || []))
      .catch(err => toast.error(err.response?.data?.message || 'Failed to load orders')).finally(() => setLoading(false));
  }, []);
  const w = q.trim().toLowerCase().split(/\s+/).filter(Boolean);
  const shown = list.filter(m => w.every(x => [m.po_no, m.project_name, m.project_code, m.client_name].join(' ').toLowerCase().includes(x)));
  const ST = { draft: 'Draft', submitted: 'Pending Approval', approved: 'Approved', rejected: 'Rejected' };
  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg flex flex-col max-h-[85vh]">
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
          <h3 className="font-bold text-gray-900">New Quotation</h3>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-gray-100"><X className="w-4 h-4" /></button>
        </div>
        <div className="px-5 py-3 border-b border-gray-100">
          <div className="flex items-center gap-2 border border-gray-200 rounded-lg px-3 py-2">
            <Search className="w-4 h-4 text-gray-400" />
            <input autoFocus value={q} onChange={e => setQ(e.target.value)} placeholder="Order no., project or client" className="flex-1 text-sm outline-none" />
          </div>
        </div>
        <div className="px-5 pt-3">
          <button onClick={() => onPick(null)}
            className="w-full flex items-center gap-3 px-3 py-3 rounded-xl border-2 border-dashed border-blue-200 hover:border-blue-400 hover:bg-blue-50 text-left">
            <Plus className="w-5 h-5 text-blue-600 shrink-0" />
            <span>
              <span className="block font-semibold text-blue-700">Start a blank quotation</span>
              <span className="block text-xs text-gray-500">No Manufacturing Order needed — type the client and items yourself</span>
            </span>
          </button>
          <p className="text-[11px] font-semibold text-gray-400 uppercase tracking-wide mt-4 mb-1 px-1">Or copy items from a Manufacturing Order</p>
        </div>
        <div className="overflow-y-auto flex-1 p-2">
          {loading ? <p className="text-center text-sm text-gray-400 py-6">Loading…</p>
            : !shown.length ? <p className="text-center text-sm text-gray-400 py-6">No Manufacturing Orders found</p>
            : shown.map(m => (
              <button key={m.id} onClick={() => onPick(m.id)} className="w-full text-left px-3 py-2.5 rounded-lg hover:bg-blue-50 flex items-center gap-3">
                <span className="flex-1 min-w-0">
                  <span className="block font-semibold text-gray-800">{m.po_no} <span className="ml-1 text-[11px] font-medium text-gray-400">{ST[m.status] || m.status}</span></span>
                  <span className="block text-xs text-gray-500 truncate">{[m.project_name, m.project_code, m.client_name].filter(Boolean).join(' · ')}</span>
                </span>
                <span className="text-[11px] text-gray-400 whitespace-nowrap">{m.item_count} items{m.quote_count ? ` · ${m.quote_count} quote${m.quote_count > 1 ? 's' : ''}` : ''}</span>
              </button>
            ))}
        </div>
      </div>
    </div>
  );
}
